import React, { useMemo, useState } from "react";
import {
  Button,
  Card,
  Col,
  Descriptions,
  Drawer,
  Form,
  Input,
  Menu,
  message,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Table,
  Tag,
} from "antd";
import { DeleteOutlined, EditOutlined, EyeOutlined, PlusOutlined, TagOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useContextItems } from "../../../../data/hooks/firestore/useContextItems";
import dayjs from "dayjs";
import Loading from "../../../../components/shared-components/Loading";
import EllipsisDropdown from "../../../../components/shared-components/EllipsisDropdown";

const { TextArea } = Input;

const ContextManagement = () => {
  const { t } = useTranslation();
  const { data: contextItems, loading, createContextItem, updateContextItem, deleteContextItem } = useContextItems();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [form] = Form.useForm();

  const handleSubmit = async (values) => {
    setSaving(true);
    try {
      const payload = {
        title: values.title,
        context: values.context,
        category: values.category,
        tags: values.tags || [],
      };

      if (editingItem) {
        await updateContextItem(editingItem.id, payload);
        message.success(t("notifications.context_item_updated"));
      } else {
        await createContextItem(payload);
        message.success(t("notifications.context_item_created"));
      }

      setIsModalVisible(false);
      resetForm();
    } catch (error) {
      console.error("Error saving context item:", error);
      message.error(t("notifications.error_saving_context_item"));
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    form.setFieldsValue({
      title: item.title,
      context: item.context,
      category: item.category,
      tags: item.tags || [],
    });
    setIsModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await deleteContextItem(id);
      message.success(t("notifications.context_item_deleted"));
    } catch (error) {
      console.error("Error deleting context item:", error);
      message.error(t("notifications.error_deleting_context_item"));
    }
  };

  const resetForm = () => {
    form.resetFields();
    setEditingItem(null);
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
    resetForm();
  };

  const showDrawer = (item) => {
    setDrawerVisible(true);
    setSelectedItem(item);
  };

  const closeDrawer = () => {
    setDrawerVisible(false);
    setSelectedItem(null);
  };

  const tableColumns = [
    {
      title: t("title"),
      dataIndex: "title",
      key: "title",
      sorter: (a, b) => a.title.localeCompare(b.title),
      render: (_, data) => (
        <button className={"text-button"} onClick={() => showDrawer(data)}>
          <span>{data.title}</span>
        </button>
      ),
    },
    {
      title: t("category"),
      dataIndex: "category",
      key: "category",
      sorter: (a, b) => a.category.localeCompare(b.category),
    },
    {
      title: t("context"),
      dataIndex: "context",
      key: "context",
      ellipsis: true,
      render: (text) => <span>{text}</span>,
    },
    {
      title: t("tags"),
      dataIndex: "tags",
      key: "tags",
      render: (tags) => (
        <>
          {tags?.map((tag, index) => (
            <Tag key={index} icon={<TagOutlined />}>
              {tag}
            </Tag>
          ))}
        </>
      ),
    },
    {
      title: t("updated_at"),
      dataIndex: "updatedAt",
      key: "updatedAt",
      render: (timestamp) => (timestamp ? dayjs.unix(timestamp).format("DD-MM-YYYY HH:mm") : "-"),
      sorter: (a, b) => (a.updatedAt || 0) - (b.updatedAt || 0),
    },
    {
      title: "",
      key: "actions",
      render: (_, record) => {
        const menu = (
          <Menu>
            <Menu.Item key="view" onClick={() => showDrawer(record)}>
              <EyeOutlined />
              <span style={{ marginLeft: 2 }}>{t("details")}</span>
            </Menu.Item>
            <Menu.Item key="edit" onClick={() => handleEdit(record)}>
              <EditOutlined />
              <span style={{ marginLeft: 2 }}>{t("edit")}</span>
            </Menu.Item>
            <Menu.Item key="delete">
              <Popconfirm
                title={t("confirm_delete_context_item")}
                onConfirm={() => handleDelete(record.id)}
                okText={t("yes")}
                cancelText={t("no")}
              >
                <span>
                  <DeleteOutlined />
                  <span style={{ marginLeft: 2 }}>{t("delete")}</span>
                </span>
              </Popconfirm>
            </Menu.Item>
          </Menu>
        );
        return <EllipsisDropdown menu={menu} />;
      },
    },
  ];

  // Collect all unique tags from existing context items
  const availableTags = useMemo(() => {
    if (!contextItems) return [];
    const tagsSet = new Set();
    contextItems.forEach((item) => {
      item.tags?.forEach((tag) => tagsSet.add(tag));
    });
    return Array.from(tagsSet).sort();
  }, [contextItems]);

  // Filter context items based on search text
  const filteredContextItems =
    contextItems?.filter((item) => {
      if (!searchText) return true;

      const searchLower = searchText.toLowerCase();
      const titleMatch = item.title?.toLowerCase().includes(searchLower);
      const categoryMatch = item.category?.toLowerCase().includes(searchLower);
      const contextMatch = item.context?.toLowerCase().includes(searchLower);
      const tagsMatch = item.tags?.some((tag) => tag.toLowerCase().includes(searchLower));

      return titleMatch || categoryMatch || contextMatch || tagsMatch;
    }) || [];

  if (loading) {
    return <Loading />;
  }

  return (
    <>
      <Row className="mb-2">
        <Col xs={24} md={16} xl={15} xxl={14}>
          <h1>{t("context_management")}</h1>
        </Col>
      </Row>

      <Row>
        <Col xs={24}>
          <Card>
            <div style={{ marginBottom: 16, display: "flex", justifyContent: "space-between", gap: 16 }}>
              <Input
                placeholder={t("search_context_items")}
                allowClear
                style={{ maxWidth: 200 }}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onSearch={(value) => setSearchText(value)}
              />
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  resetForm();
                  setIsModalVisible(true);
                }}
              >
                {t("add_context_item")}
              </Button>
            </div>
            <div className="table-responsive">
              <Table
                showSorterTooltip={false}
                columns={tableColumns}
                dataSource={filteredContextItems}
                rowKey="id"
                scroll={{ x: 1300 }}
                pagination={{ showSizeChanger: true }}
              />
            </div>
          </Card>
        </Col>
      </Row>

      <Modal
        title={editingItem ? t("edit_context_item") : t("add_context_item")}
        open={isModalVisible}
        onCancel={handleModalClose}
        footer={null}
        width={700}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item label={t("title")} name="title" rules={[{ required: true, message: t("please_enter_title") }]}>
            <Input placeholder={t("context_title_placeholder")} />
          </Form.Item>

          <Form.Item
            label={t("category")}
            name="category"
            rules={[{ required: true, message: t("please_enter_category") }]}
          >
            <Input placeholder={t("context_category_placeholder")} />
          </Form.Item>

          <Form.Item
            label={t("context")}
            name="context"
            rules={[{ required: true, message: t("please_enter_context") }]}
          >
            <TextArea rows={6} placeholder={t("context_content_placeholder")} />
          </Form.Item>

          <Form.Item label={t("tags")} name="tags">
            <Select
              mode="tags"
              placeholder={t("context_tags_placeholder")}
              options={availableTags.map((tag) => ({ label: tag, value: tag }))}
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
            <Space>
              <Button onClick={handleModalClose}>{t("cancel")}</Button>
              <Button type="primary" htmlType="submit" loading={saving}>
                {editingItem ? t("update") : t("create")}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {selectedItem && (
        <Drawer title={selectedItem.title} placement="right" onClose={closeDrawer} open={drawerVisible} width={700}>
          <Descriptions bordered size="small" column={1}>
            <Descriptions.Item label={t("title")}>{selectedItem.title}</Descriptions.Item>
            <Descriptions.Item label={t("category")}>{selectedItem.category}</Descriptions.Item>
            <Descriptions.Item label={t("context")}>
              <div style={{ whiteSpace: "pre-wrap" }}>{selectedItem.context}</div>
            </Descriptions.Item>
            <Descriptions.Item label={t("tags")}>
              {selectedItem.tags?.map((tag, index) => (
                <Tag key={index} icon={<TagOutlined />}>
                  {tag}
                </Tag>
              ))}
            </Descriptions.Item>
            <Descriptions.Item label={t("created_at")}>
              {selectedItem.createdAt ? dayjs.unix(selectedItem.createdAt).format("DD-MM-YYYY HH:mm") : "-"}
            </Descriptions.Item>
            <Descriptions.Item label={t("updated_at")}>
              {selectedItem.updatedAt ? dayjs.unix(selectedItem.updatedAt).format("DD-MM-YYYY HH:mm") : "-"}
            </Descriptions.Item>
          </Descriptions>

          <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
            <Button
              onClick={() => {
                closeDrawer();
                handleEdit(selectedItem);
              }}
            >
              {t("edit")}
            </Button>
          </div>
        </Drawer>
      )}
    </>
  );
};

export default ContextManagement;
